import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import {
  useOpportunities,
  useMoveOpportunityStage,
  useWinOpportunity,
  useLoseOpportunity,
  type Opportunity,
  type OpportunityStage,
} from './useOpportunities';

/* ── Porte de `erpone-pipeline-de-vendas-kanban` ──────────────────────────
   5 colunas por estágio de Opportunity (RF-006); colunas vazias mostram
   zero, nunca "sem estágio correspondente". Mudança de estágio por ação
   explícita que persiste (sem DnD no MVP, conforme PRD). */

interface ColumnDef {
  id: OpportunityStage;
  label: string;
  dot: string;
  next: OpportunityStage | null;
}

const COLUMNS: ColumnDef[] = [
  { id: 'PROSPECTING', label: 'Prospecção', dot: 'bg-outline', next: 'QUALIFICATION' },
  { id: 'QUALIFICATION', label: 'Qualificação', dot: 'bg-secondary-fixed-dim', next: 'PROPOSAL' },
  { id: 'PROPOSAL', label: 'Proposta', dot: 'bg-tertiary-fixed-dim', next: 'NEGOTIATION' },
  { id: 'NEGOTIATION', label: 'Negociação', dot: 'bg-primary-fixed-dim', next: null },
  { id: 'CLOSING', label: 'Fechamento', dot: 'bg-[#10b981]', next: null },
];

function initialsOf(id: string): string {
  return id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || 'OP';
}

export function PipelinePage(): JSX.Element {
  const navigate = useNavigate();
  const [ownerFilter, setOwnerFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const { data, isLoading, error } = useOpportunities({ page: 1, limit: 100 });
  const moveStage = useMoveOpportunityStage();
  const winOpp = useWinOpportunity();
  const loseOpp = useLoseOpportunity();
  const [loseId, setLoseId] = useState<string | null>(null);
  const [loseReason, setLoseReason] = useState('');

  const quotes = useMemo(() => data?.data ?? [], [data]);

  const owners = useMemo(
    () => [...new Set(quotes.map((q) => q.ownerId))].sort(),
    [quotes],
  );
  const regions = useMemo(
    () =>
      [...new Set(quotes.map((q) => q.region).filter((r): r is string => !!r))].sort(),
    [quotes],
  );

  const filtered = quotes.filter(
    (q) =>
      (!ownerFilter || q.ownerId === ownerFilter) &&
      (!regionFilter || q.region === regionFilter),
  );

  // RN-009: pipeline ativo exclui LOST; inclui abertos + WON.
  const pipelineValue = filtered
    .filter((q) => q.outcome !== 'LOST')
    .reduce((sum, q) => sum + q.amount, 0);

  const byColumn = useMemo(() => {
    const grouped = new Map<OpportunityStage, Opportunity[]>();
    for (const col of COLUMNS) grouped.set(col.id, []);
    for (const quote of filtered) {
      grouped.get(quote.stage)?.push(quote);
    }
    return grouped;
  }, [filtered]);

  const busy = moveStage.isPending || winOpp.isPending || loseOpp.isPending;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-col gap-md border-b border-outline-variant bg-surface-container-lowest px-xl py-lg sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-xs font-display-lg text-display-lg text-on-surface">
            Pipeline de Vendas
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Oportunidades do tenant por estágio do funil.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <label className="flex items-center gap-xs font-label-md text-label-md text-on-surface-variant">
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              person
            </span>
            <select
              aria-label="Filtrar por dono"
              className="rounded-md border border-outline-variant bg-surface-container-lowest px-sm py-xs"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
            >
              <option value="">All Owners</option>
              {owners.map((o) => (
                <option key={o} value={o}>
                  {o.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-xs font-label-md text-label-md text-on-surface-variant">
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
              location_on
            </span>
            <select
              aria-label="Filtrar por região"
              className="rounded-md border border-outline-variant bg-surface-container-lowest px-sm py-xs"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
            >
              <option value="">All Regions</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <div className="mr-md hidden flex-col items-end border-r border-outline-variant px-md lg:flex">
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
              Valor do pipeline
            </span>
            <span className="font-headline-sm text-headline-sm text-primary">
              {formatCurrency(pipelineValue)}
            </span>
          </div>
          <Button variant="primary" onClick={() => void navigate({ to: '/crm/opportunities/new' })}>
            Nova oportunidade
          </Button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden bg-background p-lg">
        {isLoading ? (
          <LoadingSkeleton count={4} height="180px" />
        ) : error ? (
          <p className="font-body-md text-body-md text-error">
            Erro ao carregar o pipeline: {(error as Error).message}
          </p>
        ) : (
          <div className="kanban-scroll flex h-full items-start gap-md overflow-x-auto pb-sm">
            {COLUMNS.map((col) => {
              const cards = byColumn.get(col.id) ?? [];
              const isClosing = col.id === 'CLOSING';
              return (
                <div
                  key={col.id}
                  className="flex max-h-full w-[300px] shrink-0 flex-col rounded-xl border border-outline-variant bg-surface-container-low"
                >
                  <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-xl border-b border-outline-variant bg-surface-container-highest p-sm">
                    <div className="flex items-center gap-sm">
                      <span className={`h-3 w-3 rounded-full ${col.dot}`} />
                      <h3 className="font-headline-sm text-body-lg font-semibold text-on-surface">
                        {col.label}
                      </h3>
                      <span className="rounded-full border border-outline-variant bg-surface px-xs py-[2px] font-label-sm text-label-sm text-on-surface-variant">
                        {cards.length}
                      </span>
                    </div>
                  </div>
                  <div className="kanban-scroll flex flex-1 flex-col gap-sm overflow-y-auto p-sm">
                    {cards.length === 0 ? (
                      <div className="m-sm flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-outline-variant p-xl text-center">
                        <span className="material-symbols-outlined mb-sm text-[32px] text-outline">
                          inventory_2
                        </span>
                        <p className="font-body-sm text-body-sm text-on-surface-variant">
                          {isClosing
                            ? 'Arraste um deal para marcar como ganho.'
                            : 'Nenhuma oportunidade neste estágio.'}
                        </p>
                      </div>
                    ) : (
                      cards.map((opp) => (
                        <div
                          key={opp.id}
                          className="rounded-lg border border-outline-variant bg-surface-container-lowest p-sm text-left shadow-sm"
                        >
                          <div className="mb-xs flex items-start justify-between gap-sm">
                            <h4 className="font-headline-sm text-body-md font-semibold text-on-surface">
                              {opp.title}
                            </h4>
                            <span className="shrink-0 rounded-sm border border-outline-variant bg-surface-container-high px-xs font-label-sm text-label-sm text-on-surface-variant">
                              {opp.probability}%
                            </span>
                          </div>
                          <p className="mb-sm line-clamp-1 font-body-sm text-body-sm text-on-surface-variant">
                            {opp.customerName ?? opp.customerId.slice(0, 8)}
                          </p>
                          <div className="mb-sm font-label-md text-label-md text-on-surface">
                            {formatCurrency(opp.amount)}
                          </div>
                          <div className="mt-auto flex items-center justify-between border-t border-surface-container-high pt-sm">
                            <div
                              className={`flex items-center gap-xs ${
                                opp.daysInStage >= 14 ? 'text-error' : 'text-on-surface-variant'
                              }`}
                            >
                              <span className="material-symbols-outlined text-[14px]">
                                {opp.daysInStage >= 14 ? 'warning' : 'schedule'}
                              </span>
                              <span className="font-label-sm text-label-sm">
                                {opp.daysInStage} {opp.daysInStage === 1 ? 'dia' : 'dias'} no estágio
                              </span>
                            </div>
                            <div
                              className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-container font-label-sm text-label-sm font-bold text-on-primary-container"
                              title={`Dono: ${opp.ownerId}`}
                            >
                              {initialsOf(opp.ownerId)}
                            </div>
                          </div>
                          {opp.stage !== 'CLOSING' && (
                            <div className="mt-sm flex gap-xs">
                              {col.next && (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    moveStage.mutate({ id: opp.id, stage: col.next! })
                                  }
                                  className="flex-1 rounded-md border border-outline-variant px-xs py-xs font-label-sm text-label-sm text-primary hover:bg-surface-container-low disabled:opacity-40"
                                >
                                  Avançar →
                                </button>
                              )}
                              {!col.next && (
                                <>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => winOpp.mutate({ id: opp.id })}
                                    className="flex-1 rounded-md bg-primary px-xs py-xs font-label-sm text-label-sm text-on-primary disabled:opacity-40"
                                  >
                                    Ganhar
                                  </button>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                      setLoseId(opp.id);
                                      setLoseReason('');
                                    }}
                                    className="flex-1 rounded-md border border-outline-variant px-xs py-xs font-label-sm text-label-sm text-error disabled:opacity-40"
                                  >
                                    Perder
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {opp.stage === 'CLOSING' && (
                            <p className="mt-sm font-label-sm text-label-sm text-on-surface-variant">
                              {opp.outcome === 'WON' ? 'Ganha' : 'Perdida'}
                              {opp.outcome === 'LOST' && opp.lossReason
                                ? ` — ${opp.lossReason}`
                                : ''}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loseId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-md"
          role="dialog"
          aria-modal="true"
          aria-label="Motivo da perda"
        >
          <div className="w-full max-w-md rounded-xl border border-outline-variant bg-surface-container-lowest p-lg shadow-lg">
            <h3 className="mb-sm font-headline-sm text-headline-sm text-on-surface">
              Marcar como perdida
            </h3>
            <p className="mb-sm font-body-sm text-body-sm text-on-surface-variant">
              O motivo é obrigatório (RN-005).
            </p>
            <input
              autoFocus
              className="mb-md w-full rounded-md border border-outline-variant bg-surface-container-low px-sm py-xs font-body-md text-body-md text-on-surface"
              placeholder="Ex.: preço acima do orçamento"
              value={loseReason}
              onChange={(e) => setLoseReason(e.target.value)}
            />
            <div className="flex justify-end gap-sm">
              <Button variant="outline" onClick={() => setLoseId(null)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={!loseReason.trim() || busy}
                onClick={() => {
                  if (loseId) loseOpp.mutate({ id: loseId, reason: loseReason.trim() });
                  setLoseId(null);
                }}
              >
                Confirmar perda
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .kanban-scroll::-webkit-scrollbar { height: 8px; width: 8px; }
        .kanban-scroll::-webkit-scrollbar-track { background: transparent; }
        .kanban-scroll::-webkit-scrollbar-thumb { background-color: #c4c6cf; border-radius: 4px; }
      `}</style>
    </div>
  );
}
